// src/services/auth/authService.js

import { 
  auth, 
  db, 
  storage as firebaseStorage,
  setAuthPersistence,
  logAnalyticsEvent 
} from '../../firebase';
import {
  signInWithPhoneNumber,
  signInWithCredential,
  PhoneAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  RecaptchaVerifier,
  ConfirmationResult,
  initializeRecaptchaConfig
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Secure storage equivalent using localStorage with encryption
// In production, consider using a more secure solution
class SecureStorage {
  constructor() {
    this.prefix = 'filatom_secure_';
  }

  async read(key) {
    try {
      const value = localStorage.getItem(this.prefix + key);
      return value || null;
    } catch {
      return null;
    }
  }

  async write(key, value) {
    try {
      localStorage.setItem(this.prefix + key, value);
    } catch (error) {
      console.error('SecureStorage write error:', error);
    }
  }

  async delete(key) {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.error('SecureStorage delete error:', error);
    }
  }

  async deleteAll() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('SecureStorage deleteAll error:', error);
    }
  }
}

class OtpRateLimitException extends Error {
  constructor(remaining) {
    super(`OTP rate limit exceeded. Retry in ${remaining}ms`);
    this.name = 'OtpRateLimitException';
    this.remaining = remaining;
    this.formattedRemaining = this._formatRemaining(remaining);
  }

  _formatRemaining(remaining) {
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

// Thrown when the Google popup is closed/cancelled by the user, so callers
// can distinguish "user backed out" from a real failure and skip showing
// an error toast for it.
class GoogleSignInCancelledException extends Error {
  constructor() {
    super('Google sign-in was cancelled by the user');
    this.name = 'GoogleSignInCancelledException';
  }
}

class AuthService {
  constructor() {
    this._firebaseAuth = auth;
    this._firestore = db;
    this._storage = new SecureStorage();
    this._phoneKey = 'phone';
    this._otpRateLimitKey = 'otp_rate_limit';
    this._maxRequests = 5;
    this._cooldownMinutes = 5;
    this._recaptchaVerifier = null;
    this._confirmationResult = null;
  }

  // ── Current Firebase user ─────────────────────────────────────────────────

  get firebaseCurrentUser() {
    return this._firebaseAuth.currentUser;
  }

  get authStateChanges() {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(this._firebaseAuth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  get isLoggedIn() {
    return this._firebaseAuth.currentUser !== null;
  }

  async isLoggedInAsync() {
    return this._firebaseAuth.currentUser !== null;
  }

  // ── Rate limiting ─────────────────────────────────────────────────────────

  async _readRateLimitState() {
    const raw = await this._storage.read(this._otpRateLimitKey);
    if (!raw) {
      return { count: 0, windowStart: new Date() };
    }

    try {
      const parsed = JSON.parse(raw);
      return {
        count: parsed.count || 0,
        windowStart: new Date(parsed.windowStart),
      };
    } catch {
      return { count: 0, windowStart: new Date() };
    }
  }

  async _writeRateLimitState(state) {
    const raw = JSON.stringify({
      count: state.count,
      windowStart: state.windowStart.toISOString(),
    });
    await this._storage.write(this._otpRateLimitKey, raw);
  }

  async checkOtpRateLimit() {
    const state = await this._readRateLimitState();
    const now = new Date();
    const elapsed = now.getTime() - state.windowStart.getTime();
    const cooldownMs = this._cooldownMinutes * 60 * 1000;

    if (elapsed >= cooldownMs) {
      await this._writeRateLimitState({ count: 0, windowStart: now });
      return null;
    }

    if (state.count >= this._maxRequests) {
      const remaining = cooldownMs - elapsed;
      return remaining;
    }

    return null;
  }

  async _incrementOtpCount() {
    const state = await this._readRateLimitState();
    const now = new Date();
    const elapsed = now.getTime() - state.windowStart.getTime();
    const cooldownMs = this._cooldownMinutes * 60 * 1000;

    if (elapsed >= cooldownMs) {
      await this._writeRateLimitState({ count: 1, windowStart: now });
    } else {
      await this._writeRateLimitState({
        count: state.count + 1,
        windowStart: state.windowStart,
      });
    }
  }

  // ── Recaptcha setup ──────────────────────────────────────────────────────
  // IMPORTANT: the element with this id must already exist in the DOM
  // (see PhoneAuthPage's `<div id="recaptcha-container" />`) before this
  // runs, or Firebase throws and sendFirebaseOtp's catch swallows it into
  // a generic "Failed to send code" message.
  //
  // We also forcibly wipe the container's innerHTML before creating a new
  // verifier. grecaptcha's widget library throws "reCAPTCHA has already
  // been rendered in this element" if a previous (possibly failed) render
  // left markup behind — clearing _recaptchaVerifier alone does NOT clean
  // up that DOM state, so a retry crashes without this.

   setupRecaptcha(containerId = 'recaptcha-container') {
    // 1. Wipe any previous verifier + DOM node completely
    this._resetRecaptcha(containerId);

    // 2. Create a brand-new container element (not managed by React VDOM).
    //    We append to body so invisible reCAPTCHA has a stable mount point.
    const container = document.createElement('div');
    container.id = containerId;
    document.body.appendChild(container);

    // 3. Build a fresh verifier — these are single-use in Firebase.
    this._recaptchaVerifier = new RecaptchaVerifier(
      this._firebaseAuth,
      container, // pass the HTMLElement directly, NOT the string ID
      {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          this._resetRecaptcha(containerId);
        },
      }
    );

    return this._recaptchaVerifier;
  }

  // Centralized cleanup used after failures and expirations so every path
  // clears both the JS instance and the DOM node consistently.
  _resetRecaptcha(containerId = 'recaptcha-container') {
    if (this._recaptchaVerifier) {
      try {
        this._recaptchaVerifier.clear();
      } catch {
        // clear() can throw if the widget is in a broken state — safe to ignore
      }
      this._recaptchaVerifier = null;
    }

    const container = document.getElementById(containerId);
    if (container) {
      container.remove();
    }
  }

    // ── Force-fetch reCAPTCHA config ──────────────────────────────────────────
  // Google's docs say the web SDK auto-fetches this, but some accounts have
  // hit a bug where the automatic fetch doesn't pick up a newly-enabled
  // Enterprise config in time. Call this once at app startup to force it.
  async initRecaptchaConfig() {
    try {
      await initializeRecaptchaConfig(this._firebaseAuth);
      console.log('reCAPTCHA Enterprise config initialized');
    } catch (error) {
      console.error('initializeRecaptchaConfig failed:', error);
    }
  }

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────

 async sendFirebaseOtp(phoneNumber, recaptchaContainerId = 'recaptcha-container') {
    const wait = await this.checkOtpRateLimit();
    if (wait !== null) {
      throw new OtpRateLimitException(wait);
    }

    await this._incrementOtpCount();

    // Always create a fresh verifier — they are single-use by design
    this.setupRecaptcha(recaptchaContainerId);

    try {
      const confirmationResult = await signInWithPhoneNumber(
        this._firebaseAuth,
        phoneNumber,
        this._recaptchaVerifier
      );

      this._confirmationResult = confirmationResult;

      return {
        verificationId: confirmationResult.verificationId,
        resendToken: null,
      };
    } catch (error) {
      console.error('sendFirebaseOtp error:', error);
      this._resetRecaptcha(recaptchaContainerId);
      throw error;
    }
  }

  // ── Step 1b: Resend OTP ───────────────────────────────────────────────────

  async resendFirebaseOtp(phoneNumber, recaptchaContainerId = 'recaptcha-container') {
    const wait = await this.checkOtpRateLimit();
    if (wait !== null) {
      throw new OtpRateLimitException(wait);
    }

    await this._incrementOtpCount();

    // Always create a fresh verifier
    this.setupRecaptcha(recaptchaContainerId);

    try {
      const confirmationResult = await signInWithPhoneNumber(
        this._firebaseAuth,
        phoneNumber,
        this._recaptchaVerifier
      );

      this._confirmationResult = confirmationResult;

      return {
        verificationId: confirmationResult.verificationId,
        resendToken: null,
      };
    } catch (error) {
      console.error('resendFirebaseOtp error:', error);
      this._resetRecaptcha(recaptchaContainerId);
      throw error;
    }
  }

  // ── Step 2: Verify OTP → sign in with Firebase ───────────────────────────

  async verifyFirebaseOtp({ verificationId, smsCode }) {
    try {
      // If we have a confirmation result, use it
      if (this._confirmationResult) {
        const userCredential = await this._confirmationResult.confirm(smsCode);
        const firebaseUser = userCredential.user;
        
        if (!firebaseUser) {
          throw new Error('Firebase returned a null user after sign in');
        }

        if (firebaseUser.phoneNumber) {
          await this._storage.write(this._phoneKey, firebaseUser.phoneNumber);
        }

        // Successful sign-in resets the rate limit counter
        await this._writeRateLimitState({
          count: 0,
          windowStart: new Date(),
        });

        // Clear reCAPTCHA verifier
        this._resetRecaptcha();

        // Log analytics event
        logAnalyticsEvent('login', { method: 'phone' });

        return firebaseUser;
      }

      // Fallback: use credential
      const credential = PhoneAuthProvider.credential(verificationId, smsCode);
      const userCredential = await signInWithCredential(this._firebaseAuth, credential);
      const firebaseUser = userCredential.user;

      if (!firebaseUser) {
        throw new Error('Firebase returned a null user after sign in');
      }

      if (firebaseUser.phoneNumber) {
        await this._storage.write(this._phoneKey, firebaseUser.phoneNumber);
      }

      // Successful sign-in resets the rate limit counter
      await this._writeRateLimitState({
        count: 0,
        windowStart: new Date(),
      });

      this._resetRecaptcha();

      logAnalyticsEvent('login', { method: 'phone' });

      return firebaseUser;
    } catch (error) {
      console.error('verifyFirebaseOtp error:', error);
      throw error;
    }
  }

  // ── Google sign-in ────────────────────────────────────────────────────────
  // Popup-based (no redirect round-trip, keeps the same SPA state as phone
  // auth). Firebase Auth handles new-vs-returning Google *accounts*
  // internally, but we don't rely on that — same as the phone flow, whether
  // this is a "new user" for OUR app is decided by userProfileExists(uid)
  // below, so a Google account that already has a Filatom profile always
  // lands on home instead of being re-run through onboarding.

  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(this._firebaseAuth, provider);
      const firebaseUser = userCredential.user;

      if (!firebaseUser) {
        throw new Error('Firebase returned a null user after Google sign in');
      }

      logAnalyticsEvent('login', { method: 'google' });

      return firebaseUser;
    } catch (error) {
      if (
        error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request'
      ) {
        throw new GoogleSignInCancelledException();
      }
      console.error('signInWithGoogle error:', error);
      throw error;
    }
  }

  // ── Step 3: Check if user profile exists in Firestore ─────────────────────

  async userProfileExists(uid) {
    try {
      const docRef = doc(this._firestore, 'users', uid);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() && docSnap.data()?.name !== null;
    } catch (error) {
      console.error('userProfileExists error:', error);
      return false;
    }
  }

  // ── Step 4: Generate a unique username ────────────────────────────────────

  async generateUniqueUsername(name) {
    let base = name.trim().toLowerCase();
    base = base.replace(/\s+/g, '.');
    base = base.replace(/[^a-z0-9._]/g, '');
    base = base.replace(/\.{2,}/g, '.');
    base = base.replace(/_{2,}/g, '_');
    base = base.replace(/^[._]+|[._]+$/g, '');
    
    if (!base) base = 'user';
    if (base.length > 24) base = base.substring(0, 24);

    let candidate = base;
    let suffix = 1;

    while (true) {
      const q = query(
        collection(this._firestore, 'users'),
        where('username', '==', candidate)
      );
      const snap = await getDocs(q);

      if (snap.empty) return candidate;

      candidate = `${base}${suffix}`;
      suffix++;
    }
  }

  // ── Step 5: Save user profile to Firestore ────────────────────────────────

  async saveUserProfile({
    uid,
    name,
    username,
    phone,
    profileImageUrl = '',
    syncContacts = false,
  }) {
    try {
      const userRef = doc(this._firestore, 'users', uid);
      await setDoc(userRef, {
        uid,
        name,
        username,
        phone,
        profileImage: profileImageUrl,
        syncContacts,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        stats: {
          followers: 0,
          following: 0,
          atoms: 0,
          likes: 0,
        },
        preferences: {
          notifications: true,
          darkMode: true,
        },
        onboardingComplete: false,
      }, { merge: true });

      logAnalyticsEvent('user_registered', { method: 'phone' });
    } catch (error) {
      console.error('saveUserProfile error:', error);
      throw error;
    }
  }

  // ── Upload profile image ──────────────────────────────────────────────────

  async uploadProfileImage(uid, imageFile) {
    try {
      const storageRef = ref(firebaseStorage, `profile_images/${uid}`);
      await uploadBytes(storageRef, imageFile);
      const downloadUrl = await getDownloadURL(storageRef);
      
      // Update user profile with new image
      const userRef = doc(this._firestore, 'users', uid);
      await updateDoc(userRef, {
        profileImage: downloadUrl,
        updatedAt: serverTimestamp(),
      });

      return downloadUrl;
    } catch (error) {
      console.error('uploadProfileImage error:', error);
      throw error;
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  async getSavedPhone() {
    return this._storage.read(this._phoneKey);
  }

  async getCurrentUserId() {
    return this._firebaseAuth.currentUser?.uid || null;
  }

  async getCurrentUser() {
    return this._firebaseAuth.currentUser || null;
  }

  async signOutFirebase() {
    try {
      await signOut(this._firebaseAuth);
      await this._storage.deleteAll();
      
      this._resetRecaptcha();
      this._confirmationResult = null;
      
      logAnalyticsEvent('logout');
    } catch (error) {
      console.error('signOutFirebase error:', error);
      throw error;
    }
  }

  async logout() {
    await this.signOutFirebase();
  }

  // ── Delete account ─────────────────────────────────────────────────────────

  async deleteAccount() {
    try {
      const user = this._firebaseAuth.currentUser;
      if (!user) throw new Error('No user logged in');

      // Delete user data from Firestore
      const userRef = doc(this._firestore, 'users', user.uid);
      await updateDoc(userRef, {
        isActive: false,
        deletedAt: serverTimestamp(),
      });

      // Delete the user account
      await user.delete();
      
      await this._storage.deleteAll();
      
      this._resetRecaptcha();
      this._confirmationResult = null;
      
      logAnalyticsEvent('user_deleted');
    } catch (error) {
      console.error('deleteAccount error:', error);
      throw error;
    }
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const authService = new AuthService();
export default authService;
export { authService };

// ── Exceptions ──────────────────────────────────────────────────────────────

export { OtpRateLimitException, GoogleSignInCancelledException };